/**
 * The words for a connection's life after linking (v7.1.0): what "Sync now"
 * did at each bank, whether history is still arriving, what Disconnect will
 * do, and when the app should refresh itself on return. PURE — no DOM, no
 * fetch — so every sentence is unit-tested (test/unit/connection-copy.test.ts).
 *
 * The rules every line here keeps:
 *   - an unknown is never a number (CLAUDE.md): a figure the bank has not
 *     reported is said in words, never as $0;
 *   - one bank's trouble is that bank's line, never the whole screen's;
 *   - calm, not scolding (§14): "couldn't be reached", never "failed!".
 */

import { formatMoneyExact } from './envelope-math';
import { relativeAge } from './freshness-state';
import type {
  BankSyncResult,
  ConnectionStatus,
  DisconnectImpact,
  DisconnectPreview,
  HistoryStatus,
} from './api';

const bankName = (name: string | null) => name ?? 'Your bank';

/** Statuses where only a fresh sign-in at the bank will help. */
export function needsReconnect(status: ConnectionStatus): boolean {
  return status === 'reauth_required' || status === 'revoked_by_user';
}

/** Statuses where a Reconnect button is worth offering (before it breaks, too). */
export function canReconnect(status: ConnectionStatus): boolean {
  return status !== 'ok';
}

export type LineTone = 'ok' | 'watch' | 'bad';

export interface BankLine {
  text: string;
  tone: LineTone;
}

/** One bank's line under "Sync now". */
export function describeBankSync(r: BankSyncResult, now: number = Date.now()): BankLine {
  const name = bankName(r.institutionName);
  switch (r.outcome) {
    case 'synced':
      return { text: `${name}: up to date.`, tone: 'ok' };
    case 'cooling_down': {
      const minutes = Math.max(1, Math.ceil((r.retryAfterSeconds ?? 60) / 60));
      const asked = r.lastSyncedAt
        ? `last synced ${relativeAge(Math.max(0, now - Date.parse(r.lastSyncedAt)))}`
        : 'asked a moment ago';
      return {
        text: `${name}: ${asked}. You can ask it again in ${minutes} min.`,
        tone: 'ok',
      };
    }
    case 'reconnect_needed':
      return {
        text: `${name}: needs you to sign in again. Reconnect it under Accounts.`,
        tone: 'bad',
      };
    case 'failed':
      return {
        text: `${name}: couldn't be reached just now. Nothing else was affected — try again in a few minutes.`,
        tone: 'watch',
      };
  }
}

/** The line when the banks could not be asked at all (Ballast was still read). */
export const BANKS_UNREACHABLE =
  "Couldn't ask your banks just now. The screen was still refreshed from Ballast.";

/**
 * "Still importing history", or nothing. `unknown` says nothing on purpose:
 * before a first sync the freshness line already says "not synced yet", and a
 * provider that never reports a status must not be claimed to be importing.
 */
export function historyNote(status: HistoryStatus): string | null {
  switch (status) {
    case 'not_ready':
      return 'still importing history';
    case 'initial_complete':
      return 'still importing older history';
    default:
      return null;
  }
}

/** True when any connection is still sending history (the runway card says so). */
export function anyImporting(statuses: readonly HistoryStatus[]): boolean {
  return statuses.some((s) => historyNote(s) != null);
}

/** What one entity's figures will do. */
export function impactLine(e: DisconnectImpact): BankLine {
  if (e.noAccountsLeft) {
    return {
      text: `${e.name} will have no connected account left, so its figures will show as unknown until you connect one. Its envelopes keep their balances.`,
      tone: 'watch',
    };
  }
  if (e.overAllocatedAfterMinor == null || e.freeAfterMinor == null) {
    return {
      text: `${e.name}: what's left can't be worked out, because a remaining account hasn't reported its balance.`,
      tone: 'watch',
    };
  }
  if (e.overAllocatedAfterMinor > 0) {
    return {
      text: `${e.name}: your envelopes will hold ${formatMoneyExact(e.overAllocatedAfterMinor)} more than the accounts left. Nothing is moved for you — you can take money back out of an envelope afterwards.`,
      tone: 'bad',
    };
  }
  return {
    text:
      e.freeBeforeMinor == null
        ? `${e.name}: free money will be ${formatMoneyExact(e.freeAfterMinor)}.`
        : `${e.name}: free money goes from ${formatMoneyExact(e.freeBeforeMinor)} to ${formatMoneyExact(e.freeAfterMinor)}.`,
    tone: 'ok',
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Everything the Disconnect sheet says, in order. */
export function disconnectCopy(p: DisconnectPreview): {
  title: string;
  removes: string;
  impact: BankLine[];
  keeps: string;
  slot: string;
} {
  const name = bankName(p.institutionName);
  return {
    title: `Disconnect ${name}?`,
    // v9.0.0: receipts are attached to transactions, so they go with them —
    // named here, before the tap, because a receipt is the operator's own file.
    removes:
      p.receipts > 0
        ? `This deletes ${name}'s ${plural(p.accounts, 'account', 'accounts')}, ${plural(p.transactions, 'transaction', 'transactions')} and the ${plural(p.receipts, 'receipt', 'receipts')} you attached to them from Ballast, and tells Plaid to stop reading it. It can't be undone — connecting again starts fresh.`
        : `This deletes ${name}'s ${plural(p.accounts, 'account', 'accounts')} and ${plural(p.transactions, 'transaction', 'transactions')} from Ballast, and tells Plaid to stop reading it. It can't be undone — connecting again starts fresh.`,
    impact: p.entities.map(impactLine),
    // v8.0.0: a bill tracked from this bank keeps its envelope (it is the
    // operator's); the bank's list of detected bills goes with the bank.
    keeps:
      "Kept: your envelopes (including bills you track), everything you've approved, and the senders you've taught.",
    // docs/PLAID.md, "verified": /item/remove does not free a slot.
    // True on any plan: it describes the Trial, whose cap is the one that bites.
    slot: "On Plaid's free Trial plan, which allows ten bank connections in total, disconnecting one does not give it back.",
  };
}

/**
 * Refresh on return to the app (owner, 10 Oct 2026): an installed Home Screen
 * app can sit in the background for days showing what it loaded. When it comes
 * back and its figures are older than this, it re-reads Ballast — never the
 * bank; that is still the operator's tap on "Sync now".
 */
export const REFRESH_ON_RETURN_AFTER_MS = 3 * 60_000;

export function shouldRefreshOnReturn(input: {
  visible: boolean;
  /** Epoch ms the figures on screen were loaded. */
  loadedAt: number;
  now: number;
  /** A sync, a sheet or a decision in flight: never repaint under the operator. */
  busy: boolean;
}): boolean {
  return input.visible && !input.busy && input.now - input.loadedAt >= REFRESH_ON_RETURN_AFTER_MS;
}
